import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Calendar, Flame, X, EyeOff, StickyNote, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { companyAdminCustomersAPI } from '../../lib/companyAdminApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt  = (n, d = 1) => (n == null ? '0' : Number(n).toFixed(d));
const pct  = (eaten, req) => (req > 0 ? Math.min(100, (eaten / req) * 100) : 0);
const toYMD = (d) => d.toISOString().split('T')[0];

const MEAL_ICONS = { 'pre breakfast':'🥥', breakfast:'🍳', brunch:'🥐', lunch:'🥗', 'mid lunch':'🍎', dinner:'🍽️', snack:'🍌', default:'🍴' };
const getMealIcon = (name = '') => MEAL_ICONS[name.toLowerCase()] || MEAL_ICONS.default;

/* ── Food Detail Modal ───────────────────────────────────────
   Mirrors the mobile "Food Detail" screen exactly.
─────────────────────────────────────────────────────────────*/
function FoodDetailModal({ item, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  if (!item) return null;

  const macros = [
    { label: 'Fat',     value: fmt(item.fat, 3),     emoji: '🟡', color: '#F59E0B' },
    { label: 'Protein', value: fmt(item.protein, 3), emoji: '🔴', color: '#EF4444' },
    { label: 'Carbs',   value: fmt(item.carbs, 3),   emoji: '🟢', color: '#22C55E' },
  ];

  const nutrition = [
    { label: 'Calories', value: fmt(item.calories, 1) },
    { label: 'Fat',      value: fmt(item.fat, 3) + ' g' },
    { label: 'Proteins', value: fmt(item.protein, 3) + ' g' },
    { label: 'Carbs',    value: fmt(item.carbs, 3) + ' g' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div
        className="w-full max-w-md rounded-t-3xl sm:rounded-3xl bg-card shadow-2xl app-modal-shell"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header image / close ── */}
        <div className="app-modal-header relative shrink-0">
          {item.food_photo ? (
            <img src={`${API_URL}${item.food_photo}`} alt={item.food_name}
              className="w-full h-48 object-cover" />
          ) : (
            <div className="w-full h-32 bg-gradient-to-br from-primary/10 to-muted flex items-center justify-center text-5xl">🥘</div>
          )}
          <button onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
          {/* Cal bar at top of content */}
          <div className="absolute bottom-0 left-0 right-0 bg-card rounded-t-3xl pt-4 pb-0 px-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-foreground">{item.food_name}</p>
              </div>
              <div className="flex items-center gap-1.5 bg-primary/10 rounded-2xl px-4 py-2">
                <Flame className="w-4 h-4 text-orange-400" />
                <span className="text-xs text-muted-foreground font-medium">Cal</span>
                <span className="text-2xl font-bold text-primary">{fmt(item.calories, 0)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="app-modal-body px-5 pb-6 space-y-5 pt-3">
          {/* ── Macro chips ── */}
          <div className="flex gap-2 flex-wrap">
            {macros.map(m => (
              <div key={m.label} className="flex items-center gap-1.5 rounded-full px-3 py-1.5"
                style={{ background: m.color + '18', border: `1px solid ${m.color}40` }}>
                <span className="text-sm">{m.emoji}</span>
                <span className="text-xs font-semibold" style={{ color: m.color }}>{m.value}g</span>
                <span className="text-xs text-muted-foreground">{m.label}</span>
              </div>
            ))}
          </div>

          {/* ── Intake Measurement ── */}
          <div className="rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)' }}>
            <div className="px-4 pt-3 pb-1">
              <p className="text-white font-bold text-sm tracking-wide uppercase opacity-90">Intake Measurement</p>
            </div>
            <div className="bg-card/10 mx-3 mb-3 rounded-xl px-4 py-3 grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-white/70 text-xs mb-0.5">Weight</p>
                <p className="text-white font-bold text-base">{fmt(item.weight, 1)} <span className="text-xs font-normal opacity-70">{item.weight_unit || 'g'}</span></p>
              </div>
              <div className="text-center border-x border-white/20">
                <p className="text-white/70 text-xs mb-0.5">Quantity</p>
                <p className="text-white font-bold text-base">{fmt(item.portion, 1)}</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-xs mb-0.5">Unit</p>
                <p className="text-white font-bold text-base">{item.unit || 'g'}</p>
              </div>
            </div>
          </div>

          {/* ── Nutrition Values ── */}
          <div>
            <p className="text-sm font-bold text-foreground mb-3">Nutrition Values</p>
            <div className="relative">
              {/* Dashed vertical line */}
              <div className="absolute left-5 top-5 bottom-5 w-px border-l-2 border-dashed border-primary/30" />
              <div className="space-y-0">
                {nutrition.map((n, i) => (
                  <div key={n.label} className="flex items-center gap-3 py-2.5 relative">
                    {/* Bullseye icon */}
                    <div className="w-10 h-10 rounded-full border-2 border-blue-500 bg-primary/10 flex items-center justify-center z-10 flex-shrink-0">
                      <div className="w-5 h-5 rounded-full border-2 border-blue-400 bg-card flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      </div>
                    </div>
                    <div className="flex-1 flex justify-between items-center bg-muted rounded-xl px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-blue-400">{String(i + 1).padStart(2, '0')}</span>
                        <span className="text-sm font-semibold text-foreground">{n.label}</span>
                      </div>
                      <span className="text-sm font-bold text-primary">{n.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Micro Nutrients ── */}
          <div>
            <p className="text-sm font-bold text-foreground mb-3">Micro Nutrients</p>
            <div className="flex flex-col items-center justify-center py-6 bg-muted rounded-2xl gap-2">
              <EyeOff className="w-8 h-8 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">No micro nutrient data available</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Image Modal ─────────────────────────────────────────────*/
function ImageModal({ src, alt, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.82)' }} onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-card/10 hover:bg-card/25 flex items-center justify-center transition-colors">
        <X className="w-5 h-5 text-white" />
      </button>
      <img src={src} alt={alt} onClick={(e) => e.stopPropagation()} className="max-w-[90vw] max-h-[75vh] rounded-2xl shadow-2xl object-contain" />
    </div>
  );
}

/* ── Week Strip ──────────────────────────────────────────────*/
function WeekStrip({ week, selectedDate, onSelect, onPrev, onNext, canGoNext }) {
  const selKey = toYMD(selectedDate);
  const isDark =
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark');
  const colorMap = {
    met:   { border:'#22c55e', text: isDark ? '#4ade80' : '#16a34a', bg: isDark ? 'rgba(34,197,94,0.15)' : '#f0fdf4' },
    over:  { border:'#ef4444', text: isDark ? '#f87171' : '#dc2626', bg: isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2' },
    under: { border:'#eab308', text: isDark ? '#facc15' : '#ca8a04', bg: isDark ? 'rgba(234,179,8,0.15)' : '#fefce8' },
    empty: { border: isDark ? '#475569' : '#d1d5db', text: isDark ? '#94a3b8' : '#9ca3af', bg:'transparent' },
  };
  const mutedDay = isDark ? '#94a3b8' : '#64748b';
  return (
    <div className="flex items-center bg-card rounded-2xl border border-border shadow-sm px-2 py-3 gap-1">
      <button onClick={onPrev} className="p-1.5 rounded-xl hover:bg-muted transition-colors flex-shrink-0"><ChevronLeft className="w-4 h-4 text-muted-foreground" /></button>
      <div className="flex justify-between items-end flex-1">
        {week.map((d) => {
          const isSelected = d.date === selKey;
          const c = colorMap[d.status] || colorMap.empty;
          return (
            <button key={d.date} onClick={() => onSelect(new Date(d.date + 'T12:00:00'))} className="flex flex-col items-center gap-1" title={`${d.date}: ${d.eaten}/${d.required} cal`}>
              <div style={{ width:34,height:34,borderRadius:'50%',border:`2.5px ${d.status==='empty'?'dashed':'solid'} ${c.border}`,background:isSelected?c.bg:'transparent',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:isSelected?`0 0 0 2.5px ${c.border}55`:'none',transition:'all 0.18s' }}>
                <span style={{ fontSize:12,fontWeight:isSelected?700:500,color:isSelected?c.text:(d.status==='empty'?c.text:c.text) }}>{d.day}</span>
              </div>
              <span style={{ fontSize:10,fontWeight:isSelected?700:400,color:isSelected?c.text:mutedDay }}>{d.dayNum}</span>
            </button>
          );
        })}
      </div>
      <button onClick={onNext} disabled={!canGoNext} className="p-1.5 rounded-xl hover:bg-muted transition-colors flex-shrink-0 disabled:opacity-30"><ChevronRight className="w-4 h-4 text-muted-foreground" /></button>
    </div>
  );
}

/* ── Circular Gauge ──────────────────────────────────────────*/
function CircularGauge({ eaten, required }) {
  const percentage = pct(eaten, required);
  const r = 52; const circ = 2*Math.PI*r; const dash = (percentage/100)*circ;
  return (
    <div className="flex flex-col items-center justify-center">
      <svg width="130" height="130" viewBox="0 0 130 130">
        <circle cx="65" cy="65" r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="10"/>
        <circle cx="65" cy="65" r={r} fill="none" stroke="hsl(var(--primary))" strokeWidth="10" strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} strokeDashoffset="0" transform="rotate(-90 65 65)" style={{transition:'stroke-dasharray 0.6s ease'}}/>
        <text x="65" y="62" textAnchor="middle" fontSize="18" fontWeight="700" fill="hsl(var(--foreground))">{fmt(percentage,1)}%</text>
        <text x="65" y="78" textAnchor="middle" fontSize="10" fill="hsl(var(--muted-foreground))">Calories</text>
      </svg>
    </div>
  );
}

/* ── Macro Bar ───────────────────────────────────────────────*/
function MacroBar({ label, eaten, required, color='#3B82F6' }) {
  const p = pct(eaten, required);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground font-medium">{label}</span>
        <span className="text-foreground font-semibold">{fmt(eaten)}/{fmt(required)}</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{width:`${p}%`,backgroundColor:color}}/>
      </div>
    </div>
  );
}

/* ── Meal Time Card ──────────────────────────────────────────*/
function MealCard({ meal, onFoodClick, onImageClick }) {
  const [open, setOpen] = useState(false);
  const hasIntakes = meal.intakes?.length > 0;

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      <button className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-muted transition-colors"
        onClick={() => hasIntakes && setOpen(o => !o)} data-testid={`meal-card-${meal._id}`}>
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-2xl flex-shrink-0">{getMealIcon(meal.name)}</div>
        <div className="flex-1 text-left">
          <p className="font-semibold text-foreground text-sm">{meal.name}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
            <Flame className="w-3 h-3 text-orange-400"/>{fmt(meal.totals?.calories)} Cal
          </p>
        </div>
        {hasIntakes
          ? (open ? <ChevronUp className="w-4 h-4 text-muted-foreground"/> : <ChevronDown className="w-4 h-4 text-muted-foreground"/>)
          : <span className="text-xs text-red-600 font-bold">Empty</span>}
      </button>

      {open && hasIntakes && (
        <div className="border-t border-border divide-y divide-border">
          {meal.intakes.map((item, idx) => (
            <div key={idx}
              className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-primary/10 transition-colors group"
              onClick={() => onFoodClick(item)}
              title="Click to view full nutrition details"
            >
              <div className="flex items-center gap-3">
                {item.food_photo ? (
                  <img src={`${API_URL}${item.food_photo}`} alt={item.food_name}
                    className="w-9 h-9 rounded-lg object-cover ring-1 ring-border group-hover:ring-primary/40 transition-all"
                    onClick={(e) => { e.stopPropagation(); onImageClick(`${API_URL}${item.food_photo}`, item.food_name); }}
                    onError={(e) => { e.target.style.display='none'; }}/>
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center text-base">🥘</div>
                )}
                <div>
                  <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{item.food_name}</p>
                  <p className="text-xs text-muted-foreground">{item.portion > 1 ? `×${item.portion}` : ''}{item.weight ? ` · ${item.weight}${item.unit||'g'}` : ''}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-foreground">{fmt(item.calories)} <span className="font-normal text-muted-foreground text-xs">cal</span></p>
                <p className="text-xs text-muted-foreground">C:{fmt(item.carbs)} · P:{fmt(item.protein)} · F:{fmt(item.fat)}</p>
              </div>
            </div>
          ))}
          <div className="flex justify-between px-4 py-2.5 bg-primary/10">
            <span className="text-xs font-semibold text-primary">Meal Total</span>
            <span className="text-xs font-bold text-primary">{fmt(meal.totals.calories)} cal &nbsp;|&nbsp; C:{fmt(meal.totals.carbs)} &nbsp;P:{fmt(meal.totals.protein)} &nbsp;F:{fmt(meal.totals.fat)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Dietitian day note (private to staff) ───────────────────*/
function DietitianDayNote({ customerId, dateYmd, initialNote, onSaved }) {
  const [note, setNote] = useState(initialNote || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNote(initialNote || '');
  }, [initialNote, dateYmd]);

  const dirty = (note || '').trim() !== (initialNote || '').trim();

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await companyAdminCustomersAPI.saveHistoryNote(customerId, {
        date: dateYmd,
        note,
      });
      const saved = res.data?.dietitian_note?.note ?? note.trim();
      onSaved?.(saved);
      toast.success('Dietitian note saved');
    } catch {
      toast.error('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-amber-500/10 dark:bg-amber-950/30 rounded-2xl border border-amber-500/30 dark:border-amber-700/50 shadow-sm p-4 space-y-3"
      data-testid="history-dietitian-note"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <StickyNote className="w-4 h-4 text-amber-700 dark:text-amber-400" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">Dietitian note</p>
            <p className="text-xs text-amber-800/80 dark:text-amber-300/80 flex items-center gap-1 mt-0.5">
              <Lock className="w-3 h-3" />
              Visible only to dietitian — not shown in the app
            </p>
          </div>
        </div>
        {dirty && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 rounded-xl text-xs font-semibold text-white bg-amber-700 hover:bg-amber-800 dark:bg-amber-600 dark:hover:bg-amber-500 disabled:opacity-50 transition-colors"
            data-testid="history-dietitian-note-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        placeholder='e.g. Good compliance today, protein still low at lunch — adjust tomorrow'
        className="w-full resize-y rounded-xl border border-amber-500/40 bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-amber-500/40"
        data-testid="history-dietitian-note-input"
      />
    </div>
  );
}

/* ── Main History Tab ────────────────────────────────────────*/
export default function CustomerHistoryTab({ customerId }) {
  const [date, setDate]           = useState(new Date());
  const [weekAnchor, setWeekAnchor] = useState(new Date());
  const [data, setData]           = useState(null);
  const [weekData, setWeekData]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [modalImage, setModalImage]     = useState(null);
  const [foodDetail, setFoodDetail]     = useState(null);

  const loadHistory = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.getHistory(customerId, toYMD(d));
      setData(res.data);
    } catch { toast.error('Failed to load history'); }
    finally { setLoading(false); }
  }, [customerId]);

  const loadWeekly = useCallback(async (anchor) => {
    try {
      const res = await companyAdminCustomersAPI.getWeekly(customerId, toYMD(anchor));
      setWeekData(res.data?.week || []);
    } catch {}
  }, [customerId]);

  useEffect(() => { loadHistory(date); },      [date, loadHistory]);
  useEffect(() => { loadWeekly(weekAnchor); }, [weekAnchor, loadWeekly]);

  const prevDay  = () => { const d = new Date(date); d.setDate(d.getDate()-1); setDate(d); };
  const nextDay  = () => { const d = new Date(date); d.setDate(d.getDate()+1); if (d <= new Date()) setDate(d); };
  const stripPrev = () => { const a = new Date(weekAnchor); a.setDate(a.getDate()-1); setWeekAnchor(a); };
  const stripNext = () => { const a = new Date(weekAnchor); a.setDate(a.getDate()+1); if (a <= new Date()) setWeekAnchor(a); };
  const canStripGoNext = toYMD(weekAnchor) < toYMD(new Date());

  const isToday = toYMD(date) === toYMD(new Date());
  const dateYmd = toYMD(date);
  const { targets={}, dayTotals={}, mealTimes=[] } = data || {};
  const calPct = pct(dayTotals.calories, targets.calories);
  const dietitianNote = data?.dietitian_note?.note || '';

  return (
    <div className="space-y-5" data-testid="history-tab">
      {/* Food Detail Modal */}
      {foodDetail && <FoodDetailModal item={foodDetail} onClose={() => setFoodDetail(null)} />}

      {/* Image Modal */}
      {modalImage && <ImageModal src={modalImage.src} alt={modalImage.alt} onClose={() => setModalImage(null)} />}

      {/* Date Navigation */}
      <div className="flex items-center justify-between bg-card rounded-2xl border border-border shadow-sm px-4 py-3">
        <button onClick={prevDay} className="p-2 rounded-xl hover:bg-muted transition-colors" data-testid="history-prev-day"><ChevronLeft className="w-5 h-5 text-muted-foreground"/></button>
        <button onClick={() => setShowCalendar(s=>!s)} className="flex items-center gap-2 text-foreground font-semibold hover:text-primary transition-colors" data-testid="history-date-btn">
          <Calendar className="w-4 h-4"/>
          {isToday ? 'Today' : date.toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'})}
          <span className="text-muted-foreground text-sm font-normal">{date.toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'})}</span>
        </button>
        <button onClick={nextDay} disabled={isToday} className="p-2 rounded-xl hover:bg-muted transition-colors disabled:opacity-30" data-testid="history-next-day"><ChevronRight className="w-5 h-5 text-muted-foreground"/></button>
      </div>

      {/* Week Strip */}
      {weekData.length > 0 && (
        <WeekStrip week={weekData} selectedDate={date} onSelect={d=>setDate(d)} onPrev={stripPrev} onNext={stripNext} canGoNext={canStripGoNext}/>
      )}

      {/* Calendar picker */}
      {showCalendar && (
        <div className="bg-card rounded-2xl border border-border shadow-lg p-4">
          <input type="date" max={toYMD(new Date())} value={toYMD(date)}
            onChange={e=>{setDate(new Date(e.target.value+'T12:00:00'));setShowCalendar(false);}}
            className="w-full border border-border rounded-xl px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="history-date-input"/>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin"/>
        </div>
      ) : (
        <>
          {/* Nutrition Summary Card */}
          <div className="bg-card rounded-2xl border border-border shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-center"><p className="text-2xl font-bold text-foreground">{fmt(dayTotals.calories)}</p><p className="text-xs text-muted-foreground mt-0.5">Eaten</p></div>
              <CircularGauge eaten={dayTotals.calories} required={targets.calories}/>
              <div className="text-center"><p className="text-2xl font-bold text-foreground">{fmt(targets.calories)}</p><p className="text-xs text-muted-foreground mt-0.5">Req.</p></div>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <MacroBar label="Carb"     eaten={dayTotals.carbs}    required={targets.carbs}    color="#3B82F6"/>
              <MacroBar label="Protein"  eaten={dayTotals.protein}  required={targets.protein}  color="#10B981"/>
              <MacroBar label="Fat"      eaten={dayTotals.fat}      required={targets.fat}      color="#F59E0B"/>
              <MacroBar label="Calories" eaten={dayTotals.calories} required={targets.calories} color="#6366F1"/>
            </div>
            {targets.calories > 0 && (
              <div className="mt-4 pt-3 border-t border-border">
                <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Daily Progress</span><span className="font-semibold text-primary">{calPct.toFixed(1)}%</span></div>
                <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full transition-all duration-700" style={{width:`${calPct}%`}}/></div>
              </div>
            )}
          </div>

          {/* Dietitian-only note for this day */}
          <DietitianDayNote
            customerId={customerId}
            dateYmd={dateYmd}
            initialNote={dietitianNote}
            onSaved={(saved) => {
              setData((prev) =>
                prev
                  ? {
                      ...prev,
                      dietitian_note: {
                        ...(prev.dietitian_note || {}),
                        note: saved,
                      },
                    }
                  : prev,
              );
            }}
          />

          {/* Meal Timings */}
          <div>
            <h3 className="text-sm font-bold text-foreground mb-3 px-1">Meal Timings</h3>
            {mealTimes.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground bg-card rounded-2xl border border-border">No meal data available</div>
            ) : (
              <div className="space-y-2.5">
                {mealTimes.map(meal => (
                  <MealCard key={meal._id} meal={meal}
                    onFoodClick={item => setFoodDetail(item)}
                    onImageClick={(src,alt) => setModalImage({src,alt})}/>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
